import { SearchClientType } from '../types';
import { SentryError } from '@ifixit/sentry';

/**
 * Executes searches across multiple Algolia indices and combines the results
 *
 * @param searchClient - The initialized Algolia search client
 * @param indexNames - Array of index names to search through
 * @param query - Search query string
 * @param maxHits - Optional maximum number of hits to return
 * @param filters - Optional filters to apply to the search
 * @param searchParameters - Optional additional search parameters
 */
export const multiSearchAlgolia = async <T>(
   searchClient: SearchClientType,
   indexNames: string[],
   query: string,
   maxHits?: number,
   filters?: string,
   searchParameters: Record<string, unknown> = {}
): Promise<{ hits: Array<T>; nbHits: number }> => {
   // Validate search client
   if (!searchClient) {
      throw new SentryError('Algolia search client not set up');
   }

   // Return empty results for empty queries
   if (!query.trim()) {
      return { hits: [], nbHits: 0 };
   }

   // Create promises for each index search
   const promises = indexNames.map(async indexName => {
      const index = searchClient.initIndex(indexName);
      const searchOptions = {
         hitsPerPage: maxHits,
         snippetEllipsisText: '...',
         ...(filters ? { filters } : {}),
         ...searchParameters,
      };

      const { hits, nbHits } = await index.search<T>(query, searchOptions);
      return { hits, nbHits };
   });

   try {
      // Wait for all searches to complete
      const results = await Promise.all(promises);

      // Combine results from all indices
      const combinedHits = results.flatMap(result => result.hits);
      const totalHits = results.reduce((sum, result) => sum + result.nbHits, 0);

      // Apply max hits limit if specified
      return {
         hits: maxHits ? combinedHits.slice(0, maxHits) : combinedHits,
         nbHits: totalHits,
      };
   } catch (error) {
      console.error('Error during multi-search:', error);
      return { hits: [], nbHits: 0 };
   }
};
